import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import type { PermissionOperationHint, PermissionResourceKey } from '@/access-control.config';
import { useAuth } from '@/chassis/core/auth/hooks';
import { isDevEnvironment } from '@/chassis/core/auth/dev-mode';
import { withResourceAccess } from '@/chassis/core/auth/access-control/resource-access';
import type { GuardedRouteConfig } from '@/chassis/core/auth/route-guards';
import { usePortalRepresentationSelection } from '@/domains/pages/0.1-user-pages/UserShared/representation-selection';

type LazyFactory = () => Promise<Record<string, unknown>>;

const lazyImport = <T extends React.ComponentType<any>>(importer: LazyFactory, name: string) =>
    React.lazy(async () => {
        const module = await importer();
        const Component = module[name];

        if (!Component) {
            throw new Error(`Lazy component not found: ${name}`);
        }

        return { default: Component as T };
    });

const lazyDefault = <T extends React.ComponentType<any>>(importer: () => Promise<{ default: T }>) => React.lazy(importer);

const lazyModule = (importer: LazyFactory, exportNames: string[]) =>
    exportNames.reduce<Record<string, React.ComponentType<any>>>((acc, name) => {
        acc[name] = lazyImport(importer, name);
        return acc;
    }, {});

type LazyModuleConfig<K extends string = string> = {
    key: K;
    importer: LazyFactory;
    exports: string[];
};

type LazyModuleMap<T extends readonly LazyModuleConfig<string>[]> = {
    [K in T[number]['key']]: ReturnType<typeof lazyModule>;
};

const createLazyModules = <T extends readonly LazyModuleConfig<string>[]>(configs: T): LazyModuleMap<T> =>
    configs.reduce((acc, { key, importer, exports }) => {
        acc[key as T[number]['key']] = lazyModule(importer, exports);
        return acc;
    }, {} as LazyModuleMap<T>);

const Main = lazyDefault(() => import('@/chassis/ui/theme/layout'));
const Unauthorized = lazyDefault(() => import('@/chassis/ui/components/page-commons/Unauthorized'));
const PageNotFound = lazyDefault(() => import('@/chassis/ui/components/page-commons/PageNotFound'));
const RecordNotFound = lazyDefault(() => import('@/chassis/ui/components/page-commons/RecordNotFound'));
const lazyModules = createLazyModules([
    {
        key: 'generalMediaModule',
        importer: () => import('@/domains/pages/1.5-general-module-media'),
        exports: [
            'AudiosList',
            'AudiosForm',
            'StreamVideoList',
            'StreamVideoForm',
            'StreamAudioList',
            'StreamAudioForm',
            'SubtitlesList',
            'SubtitlesForm',
            'TranscriptionsList',
            'TranscriptionsForm',
            'MediaGroupsList',
            'MediaGroupsForm',
            'MediaCategoriesList',
            'MediaCategoriesForm',
            'ImagesList',
            'ImagesForm',
            'VideosList',
            'VideosForm',
        ],
    },
    {
        key: 'notificationModule',
        importer: () => import('@/domains/pages/1.3-general-module-notification'),
        exports: [
            'NotificationTemplatesList',
            'NotificationTemplatesForm',
            'NotificationsList',
            'NotificationsForm',
            'NotificationCategoriesList',
            'NotificationCategoriesForm',
            'CommunicationsList',
            'CommunicationsForm',
            'CommunicationCategoriesList',
            'CommunicationCategoriesForm',
        ],
    },
    {
        key: 'documentModule',
        importer: () => import('@/domains/pages/1.4-general-module-document'),
        exports: [
            'DocumentTemplatesList',
            'DocumentTemplatesForm',
            'GeneratedDocumentsList',
            'GeneratedDocumentsForm',
            'DocumentCategoriesList',
            'DocumentCategoriesForm',
            'DocumentGroupsList',
            'DocumentGroupsForm',
            'DocumentSignaturesList',
            'DocumentSignaturesForm',
            'UploadedDocumentsList',
            'UploadedDocumentsForm',
            'UploadedImagesList',
            'UploadedImagesForm',
        ],
    },
    {
        key: 'ticketModule',
        importer: () => import('@/domains/pages/1.7-general-module-ticket'),
        exports: [
            'TicketsList',
            'TicketsForm',
            'TicketsEntriesList',
            'TicketsEntriesForm',
            'TicketsCategoriesList',
            'TicketsCategoriesForm',
            'TicketsPrioritiesList',
            'TicketsPrioritiesForm',
            'TicketsCriticalitiesList',
            'TicketsCriticalitiesForm',
            'TicketHandlingTeamsList',
            'TicketHandlingTeamsForm',
        ],
    },
    {
        key: 'helpModule',
        importer: () => import('@/domains/pages/1.6-general-module-help'),
        exports: ['AboutPagesList', 'AboutPagesForm', 'FaqsList', 'FaqsForm', 'HelpContactsList', 'HelpContactsForm', 'UserReviewsList', 'UserReviewsForm'],
    },
    {
        key: 'authUserModule',
        importer: () => import('@/domains/pages/0.1-user-pages/Login'),
        exports: ['LoginForm'],
    },
    {
        key: 'registerModule',
        importer: () => import('@/domains/pages/0.1-user-pages/Register'),
        exports: ['RegisterForm'],
    },
    {
        key: 'forgotPasswordModule',
        importer: () => import('@/domains/pages/2.1-auth-module-authentication/ForgotPassword'),
        exports: ['ForgotPasswordForm'],
    },
    {
        key: 'authModule',
        importer: () => import('@/domains/pages/2.1-auth-module-authentication'),
        exports: [
            'UserCredentialsList',
            'UserCredentialsForm',
            'UserLoginsList',
            'UserLoginsForm',
            'ConfirmationCodesList',
            'ConfirmationCodesForm',
            'CodeCategoriesList',
            'CodeCategoriesForm',
            'DeviceCategoriesList',
            'DeviceCategoriesForm',
            'DevicesList',
            'DevicesForm',
            'MultifactorCategoriesList',
            'MultifactorCategoriesForm',
            'MultifactorCredentialsList',
            'MultifactorCredentialsForm',
            'IPBlocksList',
            'IPBlocksForm',
            'TermsAcceptancesList',
            'TermsAcceptancesForm',
        ],
    },
    {
        key: 'authorizationModule',
        importer: () => import('@/domains/pages/2.2-auth-module-authorization'),
        exports: [
            'AccessGroupsList',
            'AccessGroupsForm',
            'RoleAssignmentsList',
            'RoleAssignmentsForm',
            'RolesPermissionsList',
            'RolesPermissionsForm',
            'UserAccountsList',
            'UserAccountsForm',
            'UserTermsList',
            'UserTermsForm',
        ],
    },
    {
        key: 'trackingModule',
        importer: () => import('@/domains/pages/2.3-auth-module-tracking'),
        exports: ['OccurrenceCategoriesList', 'OccurrenceCategoriesForm', 'OccurrencesList', 'OccurrencesForm', 'UserActivityList', 'UserActivityForm'],
    },
    {
        key: 'citizenModule',
        importer: () => import('@/domains/pages/3.1-registration-module-citizen'),
        exports: ['CitizensList', 'CitizensForm', 'CitizenPreferencesList', 'CitizenPreferencesForm', 'PublicAgencyFollowsList', 'PublicAgencyFollowsForm'],
    },
    {
        key: 'companyModule',
        importer: () => import('@/domains/pages/3.2-registration-module-company'),
        exports: [
            'SuppliersList',
            'SuppliersForm',
            'PublicAgenciesList',
            'PublicAgenciesForm',
            'PublicAgencyPreferencesList',
            'PublicAgencyPreferencesForm',
            'SupplierPreferencesList',
            'SupplierPreferencesForm',
            'PublicSectorsList',
            'PublicSectorsForm',
            'SupplyCategoriesList',
            'SupplyCategoriesForm',
        ],
    },
    {
        key: 'representationModule',
        importer: () => import('@/domains/pages/3.3-registration-module-representation'),
        exports: [
            'RepresentationsList',
            'RepresentationsForm',
            'RepresentativeProfilesList',
            'RepresentativeProfilesForm',
            'RepresentationRequestsList',
            'RepresentationRequestsForm',
            'RepresentationCategoriesList',
            'RepresentationCategoriesForm',
            'RepresentationApprovalsPage',
        ],
    },
    {
        key: 'publicTenderModule',
        importer: () => import('@/domains/pages/4.1-publictender-module-request'),
        exports: [
            'PublicTendersList',
            'PublicTendersForm',
            'PublicTendersSearchList',
            'PublicTendersSearchParticipate',
            'PublicTendersViewProposal',
            'ItemsList',
            'LotsList',
            'ItemCategoriesList',
            'ItemCategoriesForm',
            'LotCategoriesList',
            'LotCategoriesForm',
            'PublicTenderCategoriesList',
            'PublicTenderCategoriesForm',
        ],
    },
    {
        key: 'calendarModule',
        importer: () => import('@/domains/pages/4.2-publictender-module-calendar'),
        exports: [
            'CalendarView',
            'CalendarBalancesList',
            'CalendarBalancesForm',
            'CalendarMarkingCategoriesList',
            'CalendarMarkingCategoriesForm',
            'CalendarMarksList',
            'CalendarMarksForm',
            'HolidaysList',
            'HolidaysForm',
            'SchedulingCategoriesList',
            'SchedulingCategoriesForm',
            'SchedulingsList',
            'SchedulingsForm',
        ],
    },
    {
        key: 'chatModule',
        importer: () => import('@/domains/pages/4.3-publictender-module-chat'),
        exports: ['DialogCategoriesList', 'DialogCategoriesForm', 'DialogParticipantsList', 'DialogParticipantsForm', 'DialogsList', 'DialogsForm', 'MessagesList', 'MessagesForm', 'AIAssistantSettingsForm'],
    },
    {
        key: 'biddingModule',
        importer: () => import('@/domains/pages/4.4-publictender-module-bidding'),
        exports: [
            'LotAuctionSessionsList',
            'LotAuctionSessionsForm',
            'BidsList',
            'BidsForm',
            'ProposalsList',
            'ProposalsForm',
            'BiddingProcedureCategoriesList',
            'BiddingProcedureCategoriesForm',
            'AuctionSupplierDetailPage',
            'AuctioneerDetailPage',
            'CitizenDetailPage',
        ],
    },
    {
        key: 'approvalModule',
        importer: () => import('@/domains/pages/4.5-publictender-module-approval'),
        exports: [
            'ContractsList',
            'ContractsForm',
            'ContractAddendumsList',
            'ContractAddendumsForm',
            'ContractTerminationsList',
            'ContractTerminationsForm',
            'ApprovalsList',
            'ApprovalsForm',
            'AwardsList',
            'AwardsForm',
            'SelectionCriteriaCategoriesList',
            'SelectionCriteriaCategoriesForm',
        ],
    },
    {
        key: 'contestationModule',
        importer: () => import('@/domains/pages/4.6-publictender-module-contestation'),
        exports: ['AppealsList', 'AppealsForm', 'AppealResponsesList', 'AppealResponsesForm', 'AppealCategoriesList', 'AppealCategoriesForm', 'EvaluationCriteriaList', 'EvaluationCriteriaForm'],
    },
    {
        key: 'publicTenderCommunicationModule',
        importer: () => import('@/domains/pages/4.7-publictender-module-communication'),
        exports: ['AnnouncementsList', 'AnnouncementsForm', 'ClarificationsList', 'ClarificationsForm', 'ObjectionsList', 'ObjectionsForm'],
    },
    {
        key: 'subscriptionCatalogModule',
        importer: () => import('@/domains/pages/5.1-subscription-module-catalog'),
        exports: ['ServicesList', 'ServicesForm', 'ServiceCategoriesList', 'ServiceCategoriesForm', 'ServiceOffersList', 'ServiceOffersForm'],
    },
    {
        key: 'subscriptionAccountModule',
        importer: () => import('@/domains/pages/5.2-subscription-module-account'),
        exports: [
            'PaymentMethodsList',
            'PaymentMethodsForm',
            'PaymentMethodCategoriesList',
            'PaymentMethodCategoriesForm',
            'FinancialAccountsList',
            'FinancialAccountsForm',
            'FinancialBalancesList',
            'FinancialBalancesForm',
        ],
    },
    {
        key: 'subscriptionOrderModule',
        importer: () => import('@/domains/pages/5.3-subscription-module-order'),
        exports: [
            'SubscriptionPlansList',
            'SubscriptionPlansForm',
            'SubscriptionsList',
            'SubscriptionsForm',
            'OrderOriginsList',
            'OrderOriginsForm',
            'CommercializationFeesList',
            'CommercializationFeesForm',
            'CancellationsList',
            'CancellationsForm',
            'CancellationMotivesList',
            'CancellationMotivesForm',
        ],
    },
    {
        key: 'subscriptionPaymentModule',
        importer: () => import('@/domains/pages/5.4-subscription-module-payment'),
        exports: [
            'PaymentsList',
            'PaymentsForm',
            'InvoicesList',
            'InvoicesForm',
            'PaymentCapturesList',
            'PaymentCapturesForm',
            'PaymentMethodsList',
            'PaymentMethodsForm',
            'PaymentCategoriesList',
            'PaymentCategoriesForm',
            'RefundsList',
            'RefundsForm',
            'RefundReasonsList',
            'RefundReasonsForm',
        ],
    },
    {
        key: 'integrationPublicTenderModule',
        importer: () => import('@/domains/pages/6.1-integrations-module-publictender'),
        exports: [
            'PNCPCredentialsList',
            'PNCPCredentialsForm',
            'PNCPPublicationsList',
            'PNCPPublicationsForm',
            'PNCPPublicationJobsList',
            'PNCPPublicationJobsForm',
            'PNCPAttachmentsList',
            'PNCPAttachmentsForm',
            'PNCPAttemptsLogsList',
            'PNCPAttemptsLogsForm',
            'PNCPMappingsList',
            'PNCPMappingsForm',
            'PNCPWebhookEventsList',
            'PNCPWebhookEventsForm',
            'PNCPHTTPControllerList',
            'PNCPHTTPControllerForm',
        ],
    },
    {
        key: 'directa24PaymentModule',
        importer: () => import('@/domains/pages/6.12-integrations-module-directa24'),
        exports: ['PaymentReturnPage', 'ParticipationPaymentPage', 'PaymentHistoryPage', 'PaymentHistoryRoute', 'SuccessFeeChargePage', 'ManualReleasePage'],
    },
    {
        key: 'successFeeChargesModule',
        importer: () => import('@/domains/pages/4.5-publictender-module-approval/SuccessFeeCharges'),
        exports: ['SuccessFeeCharges'],
    },
    {
        key: 'agencyPricingModule',
        importer: () => import('@/domains/pages/4.1-publictender-module-request/AgencyPricing'),
        exports: ['AgencyPricing'],
    },
    {
        key: 'financialReportsModule',
        importer: () => import('@/domains/pages/4.1-publictender-module-request/FinancialReports'),
        exports: ['FinancialReports'],
    },
    {
        key: 'strategicIndicatorsModule',
        importer: () => import('@/domains/pages/4.1-publictender-module-request/StrategicIndicators'),
        exports: ['StrategicIndicators'],
    },
    {
        key: 'strategicIndicatorsAgencyListModule',
        importer: () => import('@/domains/pages/4.1-publictender-module-request/StrategicIndicators/AgencyTenderList'),
        exports: ['AgencyTenderList'],
    },
    {
        key: 'strategicIndicatorsSupplierListModule',
        importer: () => import('@/domains/pages/4.1-publictender-module-request/StrategicIndicators/SupplierTenderList'),
        exports: ['SupplierTenderList'],
    },
    {
        key: 'priceRecordsModule',
        importer: () => import('@/domains/pages/4.5-publictender-module-approval/PriceRecords'),
        exports: ['PriceRecords'],
    },
    {
        key: 'agencyOnboardingModule',
        importer: () => import('@/domains/pages/3.2-registration-module-company/AgencyOnboarding'),
        exports: ['AgencyOnboarding'],
    },
    {
        key: 'integrationSubscriptionModule',
        importer: () => import('@/domains/pages/6.2-integrations-module-subscription'),
        exports: [
            'GatewayIntegrationList',
            'GatewayIntegrationForm',
            'GatewayCustomersList',
            'GatewayCustomersForm',
            'GatewayCredentialsList',
            'GatewayCredentialsForm',
            'GatewayJobsList',
            'GatewayJobsForm',
            'GatewayAttemptLogsList',
            'GatewayAttemptLogsForm',
            'GatewayWebhooksList',
            'GatewayWebhooksForm',
            'CancellationsList',
            'CancellationsForm',
            'RefundsList',
            'RefundsForm',
            'SubscriptionsList',
            'SubscriptionsForm',
            'CancellationMotivesList',
            'CancellationMotivesForm',
            'RefundMotivesList',
            'RefundMotivesForm',
            'SubscriptionPlansList',
            'SubscriptionPlansForm',
            'PaymentsGatewayList',
            'PaymentsGatewayForm',
            'PaymentMethodsGatewayList',
            'PaymentMethodsGatewayForm',
        ],
    },
    {
        key: 'integrationTransfereGovModule',
        importer: () => import('@/domains/pages/6.9-integrations-module-transferegov'),
        exports: ['TransfereGovCredentialsList', 'TransfereGovCredentialsForm', 'TransfereGovProcessLogsList', 'TransfereGovProcessLogsForm'],
    },
    {
        key: 'userPagesModule',
        importer: () => import('@/domains/pages/0.1-user-pages'),
        exports: ['PortalDashboardHome', 'MyProfile', 'MyPaymentMethods', 'Faqs', 'MyTickets', 'MyRepresentations', 'SubscriptionPlansChoice', 'PartnerIntegrationLanding', 'AdminDashboard', 'SupportView'],
    },
    {
        key: 'digitalSignatureModule',
        importer: () => import('@/domains/pages/6.3-integrations-module-digitalsignature'),
        exports: [
            'DigitalSignatureRequestsList',
            'DigitalSignatureRequestsForm',
            'DigitalSignatureSessionsList',
            'DigitalSignatureSessionsForm',
            'DigitalSignersList',
            'DigitalSignersForm',
            'SignatureEntriesList',
            'SignatureEntriesForm',
            'DigitalDocumentsList',
            'DigitalDocumentsForm',
            'CertificatesList',
            'CertificatesForm',
            'AttemptLogsList',
            'AttemptLogsForm',
            'SignatureGatewayList',
            'SignatureGatewayForm',
        ],
    },
    {
        key: 'partnersModule',
        importer: () => import('@/domains/pages/6.6-integrations-module-partners'),
        exports: ['PartnersList', 'PartnersForm', 'PartnerCredentialsList', 'PartnerCredentialsForm'],
    },
    {
        key: 'generalParametersModule',
        importer: () => import('@/domains/pages/1.1-general-module-generalparameters'),
        exports: ['SystemParametersList', 'SystemParametersForm', 'PreferenceParametersList', 'PreferenceParametersForm', 'SystemResourcesList', 'SystemResourcesForm'],
    },
    {
        key: 'internationalizationModule',
        importer: () => import('@/domains/pages/1.2-general-module-internationalization'),
        exports: [
            'CountriesList',
            'CountriesForm',
            'CurrenciesList',
            'CurrenciesForm',
            'LocationsList',
            'LocationsForm',
            'TimezonesList',
            'TimezonesForm',
            'LanguageManagementList',
            'LanguageManagementForm',
            'TranslationKeysList',
            'TranslationKeysForm',
            'ZipCodesList',
            'ZipCodesForm',
        ],
    },
] as const);

const {
    generalMediaModule,
    notificationModule,
    documentModule,
    ticketModule,
    helpModule,
    authUserModule,
    registerModule,
    forgotPasswordModule,
    authModule,
    authorizationModule,
    trackingModule,
    citizenModule,
    companyModule,
    representationModule,
    publicTenderModule,
    calendarModule,
    chatModule,
    biddingModule,
    approvalModule,
    contestationModule,
    publicTenderCommunicationModule,
    subscriptionCatalogModule,
    subscriptionAccountModule,
    subscriptionOrderModule,
    subscriptionPaymentModule,
    integrationPublicTenderModule,
    integrationSubscriptionModule,
    directa24PaymentModule,
    agencyPricingModule,
    successFeeChargesModule,
    financialReportsModule,
    strategicIndicatorsModule,
    strategicIndicatorsAgencyListModule,
    strategicIndicatorsSupplierListModule,
    priceRecordsModule,
    agencyOnboardingModule,
    integrationTransfereGovModule,
    userPagesModule,
    digitalSignatureModule,
    partnersModule,
    generalParametersModule,
    internationalizationModule,
} = lazyModules;

// Manual do Fornecedor em HTML (item do menu AJUDA) e a versao sem layout que gera o PDF.
const manualFornecedorModule = lazyModule(() => import('@/domains/pages/0.1-user-pages/ManualFornecedor'), ['ManualFornecedorPage', 'ManualFornecedorImpressao']);

const LotsForm = lazyDefault(() => import('@/domains/pages/4.1-publictender-module-request/Lots/form'));
const ItemsForm = lazyDefault(() => import('@/domains/pages/4.1-publictender-module-request/Items/form'));

// 4.0 Fase Interna (etapa preparatoria por IA -> converter em licitacao)
const InternalPhaseProjectsPage = lazyDefault(() => import('@/domains/pages/4.0-publictender-module-internalphase/InternalPhase/ProjectsPage'));
// 5.5 Teste grátis: leads do cadastro de teste pelo site (admin global).
const TrialLeadsPage = lazyDefault(() => import('@/domains/pages/5.5-subscription-module-trial/TrialLeads'));
// 5.3 Meu plano do fornecedor: plano, valor pago, vigência, pagamentos, extrato e cancelamento.
const MyPlanPage = lazyDefault(() => import('@/domains/pages/5.3-subscription-module-order/MyPlan'));
// Pedidos de demonstração da página /demonstracao do site (admin global).
const DemoRequestsPage = lazyDefault(() => import('@/domains/pages/5.5-subscription-module-trial/DemoRequests'));
const InternalPhaseWorkspacePage = lazyDefault(() => import('@/domains/pages/4.0-publictender-module-internalphase/InternalPhase/ProjectWorkspace'));

const LogoutPage = lazyDefault(() => import('@/domains/pages/2.1-auth-module-authentication/Logout'));
const VerifyReportPage = lazyDefault(() => import('@/domains/pages/4.1-publictender-module-request/PublicTenders/VerifyReport'));
const VerifyAtaPage = lazyDefault(() => import('@/domains/pages/4.1-publictender-module-request/PublicTenders/VerifyAta'));

export type RouteConfig = GuardedRouteConfig<PermissionResourceKey>;

function GuestAwareHomeRedirect() {
    const { isGuest: guestSession } = useAuth();
    return <Navigate to={guestSession ? '/mydashboard' : '/mydashboard'} replace />;
}

function GuestBlockedRoute({ children }: { children: React.ReactElement }) {
    const { isGuest } = useAuth();
    if (isGuest) {
        return <Navigate to="/publictenders" replace />;
    }
    return children;
}

function SupplierOnlyRoute({ children }: { children: React.ReactElement }) {
    const { isGuest } = useAuth();
    const { selectedRepresentationId, representationCards, loading } = usePortalRepresentationSelection();

    if (isGuest) {
        return <Navigate to="/publictenders" replace />;
    }

    // Esperar carregamento das representações antes de decidir
    if (loading || !representationCards.length) {
        return <div className="flex items-center justify-center p-12 text-sm text-slate-500">Carregando...</div>;
    }

    const selectedCard = representationCards.find((card) => card.id === selectedRepresentationId);

    // Card selecionado ainda não carregou — aguardar em vez de redirecionar
    if (!selectedCard && selectedRepresentationId && selectedRepresentationId !== 'citizen-view' && selectedRepresentationId !== 'admin-view') {
        return <div className="flex items-center justify-center p-12 text-sm text-slate-500">Carregando representação...</div>;
    }

    const isSupplier = selectedCard?.entityType === 'supplier' || !!selectedCard?.entitySupplierId;
    if (!isSupplier) {
        return <Navigate to="/publictenders/search" replace />;
    }

    return children;
}

function PublicAgencyOnlyRoute({ children }: { children: React.ReactElement }) {
    const { isGuest } = useAuth();
    const { selectedRepresentationId, representationCards, loading } = usePortalRepresentationSelection();

    if (isGuest) {
        return <Navigate to="/publictenders" replace />;
    }

    if (loading || !representationCards.length) {
        return <div className="flex items-center justify-center p-12 text-sm text-slate-500">Carregando...</div>;
    }

    const selectedCard = representationCards.find((card) => card.id === selectedRepresentationId);

    if (!selectedCard && selectedRepresentationId && selectedRepresentationId !== 'citizen-view' && selectedRepresentationId !== 'admin-view') {
        return <div className="flex items-center justify-center p-12 text-sm text-slate-500">Carregando representação...</div>;
    }

    const isPublicAgency = selectedCard?.entityType === 'public_agency';
    if (!isPublicAgency) {
        return <Navigate to="/publictenders/search" replace />;
    }

    return children;
}

function AuctioneerOnlyRoute({ children }: { children: React.ReactElement }) {
    const { isGuest, user } = useAuth();
    const { selectedRepresentationId, representationCards, loading } = usePortalRepresentationSelection();

    if (isGuest) {
        return <Navigate to="/publictenders" replace />;
    }

    if (user?.systemAdmin) {
        return children;
    }

    // Esperar carregamento das representações antes de decidir
    if (loading || !representationCards.length) {
        return <div className="flex items-center justify-center p-12 text-sm text-slate-500">Carregando...</div>;
    }

    const selectedCard = representationCards.find((card) => card.id === selectedRepresentationId);
    if (selectedCard?.entityType !== 'public_agency') {
        return <Navigate to="/publictenders/search" replace />;
    }

    return children;
}

/**
 * CRUD administrativo de licitacao (`/publictenders`) — a lista com colunas
 * internas e o atalho "Visao do pregoeiro".
 *
 * Em dev e homolog continua aberta como sempre: e por ela que se cria o pregao de
 * teste, e la se entra como pregoeiro e como fornecedor. Em producao so o
 * administrador global entra; fornecedor e cidadao vao pra busca, que e a tela
 * deles. Reportado 09/09 (Gika): o "Voltar para Licitacoes" da tela de planos
 * jogava fornecedor direto nessa lista, em producao.
 *
 * Usa a MESMA regua do botao "Criar Pregao de Teste" (isDevEnvironment) de
 * proposito: a tela e o botao so servem juntos, entao ligam e desligam pelo mesmo
 * interruptor.
 *
 * ATENCAO: isto e protecao de NAVEGACAO, nao de dados. O GET /publictenders
 * continua devolvendo tudo pra quem chamar a API direto, porque o backend so
 * filtra por orgao quando o usuario TEM representacao de orgao. Fechar isso e
 * trabalho separado, na mesma familia do download de documento sem autorizacao.
 */
function DevOrSystemAdminRoute({ children }: { children: React.ReactElement }) {
    const { isGuest, user } = useAuth();

    if (isDevEnvironment()) return children;
    if (!isGuest && user?.systemAdmin === true) return children;

    return <Navigate to="/publictenders/search" replace />;
}

function GuardedPublicTendersList() {
    return (
        <DevOrSystemAdminRoute>
            <publicTenderModule.PublicTendersList />
        </DevOrSystemAdminRoute>
    );
}

// Restringe a rota ao administrador global. Diferente das demais OnlyRoute, não dá para
// distinguir o admin global por permissão: contas de órgão/pregoeiro já carregam
// admin-area:read (e user:update, alias de admin-area:write), então o único sinal confiável
// é a flag systemAdmin do login. Mesmo critério usado como escape em AuctioneerOnlyRoute.
function SystemAdminOnlyRoute({ children }: { children: React.ReactElement }) {
    const { isGuest, user } = useAuth();

    if (isGuest || user?.systemAdmin !== true) {
        return <Navigate to="/unauthorized" replace />;
    }

    return children;
}

type CrudConfig = {
    base: string;
    list: React.ComponentType;
    form: React.ComponentType;
    resource?: PermissionResourceKey;
    formOperation?: PermissionOperationHint;
    listOperation?: PermissionOperationHint;
};

type CrudGroup = {
    items: CrudConfig[];
    defaults: Pick<CrudConfig, 'resource' | 'formOperation' | 'listOperation'>;
};

const r = (path: string, element: React.ReactNode, resource?: PermissionResourceKey, operation?: PermissionOperationHint): RouteConfig => ({ path, element, resource, operation });

const render = (Component: React.ComponentType) => <Component />;

const crud = (base: string, list: React.ComponentType, form: React.ComponentType, resource?: PermissionResourceKey, formOperation?: PermissionOperationHint, listOperation?: PermissionOperationHint): RouteConfig[] => [
    r(base, render(list), resource, listOperation),
    r(`${base}/new`, render(form), resource, formOperation),
    r(`${base}/:id`, render(form), resource, formOperation),
];

const createCrudRoutes = (entries: CrudConfig[], defaults?: Pick<CrudConfig, 'resource' | 'formOperation' | 'listOperation'>): RouteConfig[] =>
    entries.flatMap(({ base, list, form, resource, formOperation, listOperation }) =>
        crud(base, list, form, resource ?? defaults?.resource, formOperation ?? defaults?.formOperation, listOperation ?? defaults?.listOperation)
    );

const generalParametersCrud: CrudConfig[] = [
    { base: 'systemparameters', list: generalParametersModule.SystemParametersList, form: generalParametersModule.SystemParametersForm },
    { base: 'preferenceparameters', list: generalParametersModule.PreferenceParametersList, form: generalParametersModule.PreferenceParametersForm },
    { base: 'systemresources', list: generalParametersModule.SystemResourcesList, form: generalParametersModule.SystemResourcesForm },
];

const internationalizationCrud: CrudConfig[] = [
    { base: 'countries', list: internationalizationModule.CountriesList, form: internationalizationModule.CountriesForm },
    { base: 'currencies', list: internationalizationModule.CurrenciesList, form: internationalizationModule.CurrenciesForm },
    { base: 'locations', list: internationalizationModule.LocationsList, form: internationalizationModule.LocationsForm },
    { base: 'timezones', list: internationalizationModule.TimezonesList, form: internationalizationModule.TimezonesForm },
    { base: 'languagemanagement', list: internationalizationModule.LanguageManagementList, form: internationalizationModule.LanguageManagementForm },
    { base: 'translationkeys', list: internationalizationModule.TranslationKeysList, form: internationalizationModule.TranslationKeysForm },
    { base: 'zipcodes', list: internationalizationModule.ZipCodesList, form: internationalizationModule.ZipCodesForm },
];

const notificationCrud: CrudConfig[] = [
    { base: 'notifications', list: notificationModule.NotificationsList, form: notificationModule.NotificationsForm },
    { base: 'notificationtemplates', list: notificationModule.NotificationTemplatesList, form: notificationModule.NotificationTemplatesForm },
    { base: 'notificationcategories', list: notificationModule.NotificationCategoriesList, form: notificationModule.NotificationCategoriesForm },
    { base: 'communications', list: notificationModule.CommunicationsList, form: notificationModule.CommunicationsForm },
    { base: 'communicationcategories', list: notificationModule.CommunicationCategoriesList, form: notificationModule.CommunicationCategoriesForm },
];

const documentCrud: CrudConfig[] = [
    { base: 'documenttemplates', list: documentModule.DocumentTemplatesList, form: documentModule.DocumentTemplatesForm },
    { base: 'generateddocuments', list: documentModule.GeneratedDocumentsList, form: documentModule.GeneratedDocumentsForm },
    { base: 'documentcategories', list: documentModule.DocumentCategoriesList, form: documentModule.DocumentCategoriesForm },
    { base: 'documentgroups', list: documentModule.DocumentGroupsList, form: documentModule.DocumentGroupsForm },
    { base: 'documentsignatures', list: documentModule.DocumentSignaturesList, form: documentModule.DocumentSignaturesForm },
    { base: 'uploadeddocuments', list: documentModule.UploadedDocumentsList, form: documentModule.UploadedDocumentsForm },
    { base: 'uploadedimages', list: documentModule.UploadedImagesList, form: documentModule.UploadedImagesForm },
];

const mediaCrud: CrudConfig[] = [
    { base: 'mediagroups', list: generalMediaModule.MediaGroupsList, form: generalMediaModule.MediaGroupsForm },
    { base: 'mediacategories', list: generalMediaModule.MediaCategoriesList, form: generalMediaModule.MediaCategoriesForm },
    { base: 'images', list: generalMediaModule.ImagesList, form: generalMediaModule.ImagesForm },
    { base: 'videos', list: generalMediaModule.VideosList, form: generalMediaModule.VideosForm },
    { base: 'streamvideo', list: generalMediaModule.StreamVideoList, form: generalMediaModule.StreamVideoForm },
    { base: 'streamaudio', list: generalMediaModule.StreamAudioList, form: generalMediaModule.StreamAudioForm },
    { base: 'audios', list: generalMediaModule.AudiosList, form: generalMediaModule.AudiosForm },
    { base: 'subtitles', list: generalMediaModule.SubtitlesList, form: generalMediaModule.SubtitlesForm },
    { base: 'transcriptions', list: generalMediaModule.TranscriptionsList, form: generalMediaModule.TranscriptionsForm },
];

const helpCenterCrud: CrudConfig[] = [
    { base: 'aboutpages', list: helpModule.AboutPagesList, form: helpModule.AboutPagesForm },
    { base: 'faqs', list: helpModule.FaqsList, form: helpModule.FaqsForm },
    { base: 'helpcontacts', list: helpModule.HelpContactsList, form: helpModule.HelpContactsForm },
    { base: 'userreviews', list: helpModule.UserReviewsList, form: helpModule.UserReviewsForm },
];

const ticketCrud: CrudConfig[] = [
    { base: 'tickets', list: ticketModule.TicketsList, form: ticketModule.TicketsForm },
    { base: 'ticketsentries', list: ticketModule.TicketsEntriesList, form: ticketModule.TicketsEntriesForm },
    { base: 'ticketscategories', list: ticketModule.TicketsCategoriesList, form: ticketModule.TicketsCategoriesForm },
    { base: 'ticketspriorities', list: ticketModule.TicketsPrioritiesList, form: ticketModule.TicketsPrioritiesForm },
    { base: 'ticketscriticalities', list: ticketModule.TicketsCriticalitiesList, form: ticketModule.TicketsCriticalitiesForm },
    { base: 'tickethandlingteams', list: ticketModule.TicketHandlingTeamsList, form: ticketModule.TicketHandlingTeamsForm },
];

const authAuthenticationCrud: CrudConfig[] = [
    { base: 'usercredentials', list: authModule.UserCredentialsList, form: authModule.UserCredentialsForm },
    { base: 'userlogins', list: authModule.UserLoginsList, form: authModule.UserLoginsForm },
    { base: 'confirmationcodes', list: authModule.ConfirmationCodesList, form: authModule.ConfirmationCodesForm },
    { base: 'codecategories', list: authModule.CodeCategoriesList, form: authModule.CodeCategoriesForm },
    { base: 'devicecategories', list: authModule.DeviceCategoriesList, form: authModule.DeviceCategoriesForm },
    { base: 'devices', list: authModule.DevicesList, form: authModule.DevicesForm },
    { base: 'multifactorcategories', list: authModule.MultifactorCategoriesList, form: authModule.MultifactorCategoriesForm },
    { base: 'multifactorcredentials', list: authModule.MultifactorCredentialsList, form: authModule.MultifactorCredentialsForm },
    { base: 'ipblocks', list: authModule.IPBlocksList, form: authModule.IPBlocksForm },
    { base: 'termsacceptances', list: authModule.TermsAcceptancesList, form: authModule.TermsAcceptancesForm },
];

const authAuthorizationCrud: CrudConfig[] = [
    { base: 'accessgroups', list: authorizationModule.AccessGroupsList, form: authorizationModule.AccessGroupsForm },
    { base: 'roleassignments', list: authorizationModule.RoleAssignmentsList, form: authorizationModule.RoleAssignmentsForm },
    { base: 'rolespermissions', list: authorizationModule.RolesPermissionsList, form: authorizationModule.RolesPermissionsForm },
    { base: 'useraccounts', list: authorizationModule.UserAccountsList, form: authorizationModule.UserAccountsForm },
    { base: 'userterms', list: authorizationModule.UserTermsList, form: authorizationModule.UserTermsForm },
];

const authTrackingCrud: CrudConfig[] = [
    { base: 'occurrencecategories', list: trackingModule.OccurrenceCategoriesList, form: trackingModule.OccurrenceCategoriesForm },
    { base: 'occurrences', list: trackingModule.OccurrencesList, form: trackingModule.OccurrencesForm },
    { base: 'useractivity', list: trackingModule.UserActivityList, form: trackingModule.UserActivityForm },
];

const citizenCrud: CrudConfig[] = [
    { base: 'citizens', list: citizenModule.CitizensList, form: citizenModule.CitizensForm },
    { base: 'citizenpreferences', list: citizenModule.CitizenPreferencesList, form: citizenModule.CitizenPreferencesForm },
    { base: 'publicagencyfollows', list: citizenModule.PublicAgencyFollowsList, form: citizenModule.PublicAgencyFollowsForm },
];

const companyCrud: CrudConfig[] = [
    { base: 'suppliers', list: companyModule.SuppliersList, form: companyModule.SuppliersForm },
    // 'publicagencies' (cadastro global de órgãos públicos) NÃO é gerado aqui: ele herdaria
    // o resource registrationCompany, que pregoeiros/órgãos/fornecedores possuem, permitindo
    // acesso indevido via URL direta. Ele é definido explicitamente em specialRoutes com
    // SystemAdminOnlyRoute (apenas administrador global). A auto-edição do próprio órgão
    // continua em /publicagencies/self/:id (resource userPages).
    { base: 'publicagencypreferences', list: companyModule.PublicAgencyPreferencesList, form: companyModule.PublicAgencyPreferencesForm },
    { base: 'supplierpreferences', list: companyModule.SupplierPreferencesList, form: companyModule.SupplierPreferencesForm },
    { base: 'publicsectors', list: companyModule.PublicSectorsList, form: companyModule.PublicSectorsForm },
    { base: 'supplycategories', list: companyModule.SupplyCategoriesList, form: companyModule.SupplyCategoriesForm },
];

const representationCrud: CrudConfig[] = [
    { base: 'representations', list: representationModule.RepresentationsList, form: representationModule.RepresentationsForm },
    {
        base: 'representativeprofiles',
        list: representationModule.RepresentativeProfilesList,
        form: representationModule.RepresentativeProfilesForm,
    },
    {
        base: 'representationcategories',
        list: representationModule.RepresentationCategoriesList,
        form: representationModule.RepresentationCategoriesForm,
    },
    {
        base: 'representationrequests/admin',
        list: representationModule.RepresentationRequestsList,
        form: representationModule.RepresentationRequestsForm,
    },
];

const publicTenderCrud: CrudConfig[] = [
    { base: 'publictenders', list: GuardedPublicTendersList, form: publicTenderModule.PublicTendersForm },
    { base: 'lots', list: publicTenderModule.LotsList, form: LotsForm },
    { base: 'items', list: publicTenderModule.ItemsList, form: ItemsForm },
    { base: 'publictendercategories', list: publicTenderModule.PublicTenderCategoriesList, form: publicTenderModule.PublicTenderCategoriesForm },
    { base: 'itemcategories', list: publicTenderModule.ItemCategoriesList, form: publicTenderModule.ItemCategoriesForm },
    { base: 'lotcategories', list: publicTenderModule.LotCategoriesList, form: publicTenderModule.LotCategoriesForm },
];

const calendarCrud: CrudConfig[] = [
    { base: 'calendarbalances', list: calendarModule.CalendarBalancesList, form: calendarModule.CalendarBalancesForm },
    { base: 'calendarmarks', list: calendarModule.CalendarMarksList, form: calendarModule.CalendarMarksForm },
    { base: 'calendarmarkingcategories', list: calendarModule.CalendarMarkingCategoriesList, form: calendarModule.CalendarMarkingCategoriesForm },
    { base: 'holidays', list: calendarModule.HolidaysList, form: calendarModule.HolidaysForm },
    { base: 'schedulingcategories', list: calendarModule.SchedulingCategoriesList, form: calendarModule.SchedulingCategoriesForm },
    { base: 'schedulings', list: calendarModule.SchedulingsList, form: calendarModule.SchedulingsForm },
];

const biddingCrud: CrudConfig[] = [
    { base: 'lotauctionsessions', list: biddingModule.LotAuctionSessionsList, form: biddingModule.LotAuctionSessionsForm },
    { base: 'bids', list: biddingModule.BidsList, form: biddingModule.BidsForm },
    { base: 'proposals', list: biddingModule.ProposalsList, form: biddingModule.ProposalsForm },
    { base: 'biddingprocedurecategories', list: biddingModule.BiddingProcedureCategoriesList, form: biddingModule.BiddingProcedureCategoriesForm },
];

const approvalCrud: CrudConfig[] = [
    { base: 'contracts', list: approvalModule.ContractsList, form: approvalModule.ContractsForm },
    { base: 'contractaddendums', list: approvalModule.ContractAddendumsList, form: approvalModule.ContractAddendumsForm },
    { base: 'contractterminations', list: approvalModule.ContractTerminationsList, form: approvalModule.ContractTerminationsForm },
    { base: 'approvals', list: approvalModule.ApprovalsList, form: approvalModule.ApprovalsForm },
    { base: 'awards', list: approvalModule.AwardsList, form: approvalModule.AwardsForm },
    { base: 'selectioncriteriacategories', list: approvalModule.SelectionCriteriaCategoriesList, form: approvalModule.SelectionCriteriaCategoriesForm },
];

const contestationCrud: CrudConfig[] = [
    { base: 'appeals', list: contestationModule.AppealsList, form: contestationModule.AppealsForm },
    { base: 'appealresponses', list: contestationModule.AppealResponsesList, form: contestationModule.AppealResponsesForm },
    { base: 'appealcategories', list: contestationModule.AppealCategoriesList, form: contestationModule.AppealCategoriesForm },
    { base: 'evaluationcriteria', list: contestationModule.EvaluationCriteriaList, form: contestationModule.EvaluationCriteriaForm },
];

const publicTenderCommunicationCrud: CrudConfig[] = [
    { base: 'communications/announcements', list: publicTenderCommunicationModule.AnnouncementsList, form: publicTenderCommunicationModule.AnnouncementsForm },
    { base: 'communications/clarifications', list: publicTenderCommunicationModule.ClarificationsList, form: publicTenderCommunicationModule.ClarificationsForm },
    { base: 'communications/objections', list: publicTenderCommunicationModule.ObjectionsList, form: publicTenderCommunicationModule.ObjectionsForm },
];

const subscriptionCatalogCrud: CrudConfig[] = [
    { base: 'services', list: subscriptionCatalogModule.ServicesList, form: subscriptionCatalogModule.ServicesForm },
    { base: 'categories', list: subscriptionCatalogModule.ServiceCategoriesList, form: subscriptionCatalogModule.ServiceCategoriesForm },
    { base: 'offers', list: subscriptionCatalogModule.ServiceOffersList, form: subscriptionCatalogModule.ServiceOffersForm },
];

const subscriptionAccountCrud: CrudConfig[] = [
    { base: 'paymentmethods', list: subscriptionAccountModule.PaymentMethodsList, form: subscriptionAccountModule.PaymentMethodsForm },
    {
        base: 'paymentmethodcategories',
        list: subscriptionAccountModule.PaymentMethodCategoriesList,
        form: subscriptionAccountModule.PaymentMethodCategoriesForm,
    },
    { base: 'financialaccounts', list: subscriptionAccountModule.FinancialAccountsList, form: subscriptionAccountModule.FinancialAccountsForm },
    { base: 'financialbalances', list: subscriptionAccountModule.FinancialBalancesList, form: subscriptionAccountModule.FinancialBalancesForm },
];

const subscriptionOrderCrud: CrudConfig[] = [
    { base: 'subscriptionplans', list: subscriptionOrderModule.SubscriptionPlansList, form: subscriptionOrderModule.SubscriptionPlansForm },
    { base: 'subscriptions', list: subscriptionOrderModule.SubscriptionsList, form: subscriptionOrderModule.SubscriptionsForm },
    { base: 'origins', list: subscriptionOrderModule.OrderOriginsList, form: subscriptionOrderModule.OrderOriginsForm },
    { base: 'fees', list: subscriptionOrderModule.CommercializationFeesList, form: subscriptionOrderModule.CommercializationFeesForm },
    { base: 'cancellations', list: subscriptionOrderModule.CancellationsList, form: subscriptionOrderModule.CancellationsForm },
    { base: 'cancellationmotives', list: subscriptionOrderModule.CancellationMotivesList, form: subscriptionOrderModule.CancellationMotivesForm },
];

const subscriptionPaymentCrud: CrudConfig[] = [
    { base: 'payments', list: subscriptionPaymentModule.PaymentsList, form: subscriptionPaymentModule.PaymentsForm },
    { base: 'invoices', list: subscriptionPaymentModule.InvoicesList, form: subscriptionPaymentModule.InvoicesForm },
    { base: 'paymentcaptures', list: subscriptionPaymentModule.PaymentCapturesList, form: subscriptionPaymentModule.PaymentCapturesForm },
    { base: 'paymentmethods', list: subscriptionPaymentModule.PaymentMethodsList, form: subscriptionPaymentModule.PaymentMethodsForm },
    { base: 'paymentcategories', list: subscriptionPaymentModule.PaymentCategoriesList, form: subscriptionPaymentModule.PaymentCategoriesForm },
    { base: 'refunds', list: subscriptionPaymentModule.RefundsList, form: subscriptionPaymentModule.RefundsForm },
    { base: 'refundreasons', list: subscriptionPaymentModule.RefundReasonsList, form: subscriptionPaymentModule.RefundReasonsForm },
];

const integrationPublicTenderCrud: CrudConfig[] = [
    { base: 'pncpcredentials', list: integrationPublicTenderModule.PNCPCredentialsList, form: integrationPublicTenderModule.PNCPCredentialsForm },
    { base: 'pncppublications', list: integrationPublicTenderModule.PNCPPublicationsList, form: integrationPublicTenderModule.PNCPPublicationsForm },
    { base: 'pncppublication-jobs', list: integrationPublicTenderModule.PNCPPublicationJobsList, form: integrationPublicTenderModule.PNCPPublicationJobsForm },
    { base: 'pncpattachments', list: integrationPublicTenderModule.PNCPAttachmentsList, form: integrationPublicTenderModule.PNCPAttachmentsForm },
    { base: 'pncpattemptslogs', list: integrationPublicTenderModule.PNCPAttemptsLogsList, form: integrationPublicTenderModule.PNCPAttemptsLogsForm },
    { base: 'pncpmappings', list: integrationPublicTenderModule.PNCPMappingsList, form: integrationPublicTenderModule.PNCPMappingsForm },
    { base: 'pncpwebhookevents', list: integrationPublicTenderModule.PNCPWebhookEventsList, form: integrationPublicTenderModule.PNCPWebhookEventsForm },
    { base: 'pncphttpcontroller', list: integrationPublicTenderModule.PNCPHTTPControllerList, form: integrationPublicTenderModule.PNCPHTTPControllerForm },
];

const integrationSubscriptionCrud: CrudConfig[] = [
    { base: 'cancellationsintegrations', list: integrationSubscriptionModule.CancellationsList, form: integrationSubscriptionModule.CancellationsForm },
    { base: 'refundsintegrations', list: integrationSubscriptionModule.RefundsList, form: integrationSubscriptionModule.RefundsForm },
    { base: 'subscriptionsintegrations', list: integrationSubscriptionModule.SubscriptionsList, form: integrationSubscriptionModule.SubscriptionsForm },
    {
        base: 'cancellationmotivesintegrations',
        list: integrationSubscriptionModule.CancellationMotivesList,
        form: integrationSubscriptionModule.CancellationMotivesForm,
    },
    { base: 'refundmotivesintegrations', list: integrationSubscriptionModule.RefundMotivesList, form: integrationSubscriptionModule.RefundMotivesForm },
    { base: 'subscriptionplansintegrations', list: integrationSubscriptionModule.SubscriptionPlansList, form: integrationSubscriptionModule.SubscriptionPlansForm },
    { base: 'paymentsgateway', list: integrationSubscriptionModule.PaymentsGatewayList, form: integrationSubscriptionModule.PaymentsGatewayForm },
    { base: 'paymentmethodsgateway', list: integrationSubscriptionModule.PaymentMethodsGatewayList, form: integrationSubscriptionModule.PaymentMethodsGatewayForm },
    { base: 'gatewayintegration', list: integrationSubscriptionModule.GatewayIntegrationList, form: integrationSubscriptionModule.GatewayIntegrationForm },
    { base: 'gatewaycustomers', list: integrationSubscriptionModule.GatewayCustomersList, form: integrationSubscriptionModule.GatewayCustomersForm },
    { base: 'gatewaycredentials', list: integrationSubscriptionModule.GatewayCredentialsList, form: integrationSubscriptionModule.GatewayCredentialsForm },
    { base: 'gatewayjobs', list: integrationSubscriptionModule.GatewayJobsList, form: integrationSubscriptionModule.GatewayJobsForm },
    { base: 'gatewayattempt-logs', list: integrationSubscriptionModule.GatewayAttemptLogsList, form: integrationSubscriptionModule.GatewayAttemptLogsForm },
    { base: 'gatewaywebhooks', list: integrationSubscriptionModule.GatewayWebhooksList, form: integrationSubscriptionModule.GatewayWebhooksForm },
];

const integrationTransfereGovCrud: CrudConfig[] = [
    { base: 'transferegovcredentials', list: integrationTransfereGovModule.TransfereGovCredentialsList, form: integrationTransfereGovModule.TransfereGovCredentialsForm },
    { base: 'transferegovprocesslogs', list: integrationTransfereGovModule.TransfereGovProcessLogsList, form: integrationTransfereGovModule.TransfereGovProcessLogsForm },
];

const digitalSignatureIntegrationCrud: CrudConfig[] = [
    { base: 'digitalsignaturerequests', list: digitalSignatureModule.DigitalSignatureRequestsList, form: digitalSignatureModule.DigitalSignatureRequestsForm },
    { base: 'digitalsignaturesessions', list: digitalSignatureModule.DigitalSignatureSessionsList, form: digitalSignatureModule.DigitalSignatureSessionsForm },
    { base: 'digitalsigners', list: digitalSignatureModule.DigitalSignersList, form: digitalSignatureModule.DigitalSignersForm },
    { base: 'signatureentries', list: digitalSignatureModule.SignatureEntriesList, form: digitalSignatureModule.SignatureEntriesForm },
    { base: 'digitaldocuments', list: digitalSignatureModule.DigitalDocumentsList, form: digitalSignatureModule.DigitalDocumentsForm },
    { base: 'certificates', list: digitalSignatureModule.CertificatesList, form: digitalSignatureModule.CertificatesForm },
    { base: 'attemptlogs', list: digitalSignatureModule.AttemptLogsList, form: digitalSignatureModule.AttemptLogsForm },
    { base: 'signaturegateway', list: digitalSignatureModule.SignatureGatewayList, form: digitalSignatureModule.SignatureGatewayForm },
];

const integrationPartnersCrud: CrudConfig[] = [
    { base: 'partners', list: partnersModule.PartnersList, form: partnersModule.PartnersForm },
    { base: 'partnercredentials', list: partnersModule.PartnerCredentialsList, form: partnersModule.PartnerCredentialsForm },
];

const crudGroups: CrudGroup[] = [
    { items: generalParametersCrud, defaults: { resource: 'generalParameters', formOperation: 'write', listOperation: 'read' } },
    { items: internationalizationCrud, defaults: { resource: 'internationalization', formOperation: 'write', listOperation: 'read' } },
    { items: notificationCrud, defaults: { resource: 'notifications', formOperation: 'manage', listOperation: 'manage' } },
    { items: documentCrud, defaults: { resource: 'documents', formOperation: 'write', listOperation: 'read' } },
    { items: mediaCrud, defaults: { resource: 'media', formOperation: 'write', listOperation: 'read' } },
    { items: helpCenterCrud, defaults: { resource: 'helpCenter', formOperation: 'write', listOperation: 'read' } },
    { items: ticketCrud, defaults: { resource: 'tickets', formOperation: 'write', listOperation: 'read' } },
    { items: authAuthenticationCrud, defaults: { resource: 'authAuthentication', formOperation: 'write', listOperation: 'read' } },
    { items: authAuthorizationCrud, defaults: { resource: 'authAuthorization', formOperation: 'write', listOperation: 'read' } },
    { items: authTrackingCrud, defaults: { resource: 'authTracking', formOperation: 'write', listOperation: 'read' } },
    { items: citizenCrud, defaults: { resource: 'registrationCitizen', formOperation: 'write', listOperation: 'read' } },
    { items: companyCrud, defaults: { resource: 'registrationCompany', formOperation: 'write', listOperation: 'read' } },
    { items: representationCrud, defaults: { resource: 'registrationRepresentation', formOperation: 'write', listOperation: 'read' } },
    { items: publicTenderCrud, defaults: { resource: 'publicTenderForm', formOperation: 'write', listOperation: 'read' } },
    { items: calendarCrud, defaults: { resource: 'publicTenderCalendar', formOperation: 'write', listOperation: 'read' } },
    { items: biddingCrud, defaults: { resource: 'publicTenderBidding', formOperation: 'write', listOperation: 'read' } },
    { items: approvalCrud, defaults: { resource: 'publicTenderApproval', formOperation: 'write', listOperation: 'read' } },
    { items: contestationCrud, defaults: { resource: 'publicTenderContestation', formOperation: 'write', listOperation: 'read' } },
    { items: publicTenderCommunicationCrud, defaults: { resource: 'publicTenderForm', formOperation: 'write', listOperation: 'read' } },
    { items: subscriptionCatalogCrud, defaults: { resource: 'subscriptionCatalog', formOperation: 'write', listOperation: 'read' } },
    { items: subscriptionAccountCrud, defaults: { resource: 'subscriptionAccount', formOperation: 'write', listOperation: 'read' } },
    { items: subscriptionOrderCrud, defaults: { resource: 'subscriptionOrder', formOperation: 'write', listOperation: 'read' } },
    { items: subscriptionPaymentCrud, defaults: { resource: 'subscriptionPayment', formOperation: 'write', listOperation: 'read' } },
    { items: integrationPublicTenderCrud, defaults: { resource: 'integrationPublicTender', formOperation: 'write', listOperation: 'read' } },
    { items: integrationSubscriptionCrud, defaults: { resource: 'integrationSubscription', formOperation: 'write', listOperation: 'read' } },
    { items: integrationTransfereGovCrud, defaults: { resource: 'integrationPublicTender', formOperation: 'write', listOperation: 'read' } },
    { items: digitalSignatureIntegrationCrud, defaults: { resource: 'integrationDigitalSignature', formOperation: 'write', listOperation: 'read' } },
    { items: integrationPartnersCrud, defaults: { resource: 'integrationPartners', formOperation: 'write', listOperation: 'read' } },
];

function PublicAuthLayout() {
    return <Outlet />;
}

const commonRoutes: RouteConfig[] = [
    r('myfaqs', <userPagesModule.Faqs />, 'userPages'),
    r('manual-do-fornecedor', <manualFornecedorModule.ManualFornecedorPage />, 'userPages'),
    r(
        'mytickets',
        <GuestBlockedRoute>
            <userPagesModule.MyTickets />
        </GuestBlockedRoute>,
        'userPages'
    ),
    r(
        'myaccountdetails',
        <GuestBlockedRoute>
            <userPagesModule.MyPaymentMethods />
        </GuestBlockedRoute>,
        'userPages'
    ),
    r(
        'historico-pagamentos',
        <GuestBlockedRoute>
            {/* fornecedor vai para a aba Pagamentos de Planos (29/10); o admin fica na página avulsa */}
            <directa24PaymentModule.PaymentHistoryRoute />
        </GuestBlockedRoute>,
        'userPages'
    ),
    r('mydashboard', <userPagesModule.PortalDashboardHome />, 'userPages'),
    r(
        'myprofile',
        <GuestBlockedRoute>
            <userPagesModule.MyProfile />
        </GuestBlockedRoute>,
        'userPages'
    ),
    r(
        'admin/dashboard',
        <GuestBlockedRoute>
            <userPagesModule.AdminDashboard />
        </GuestBlockedRoute>,
        'adminArea'
    ),
    r(
        'admin/support',
        <GuestBlockedRoute>
            <userPagesModule.SupportView />
        </GuestBlockedRoute>,
        'adminArea'
    ),
];

const specialRoutes: RouteConfig[] = [
    r('authmoduletracking/occurrences', <trackingModule.OccurrencesList />, 'authTracking'),
    r('authmoduletracking/occurrences/new', <trackingModule.OccurrencesForm />, 'authTracking', 'write'),
    r('authmoduletracking/occurrences/:id', <trackingModule.OccurrencesForm />, 'authTracking', 'write'),

    r(
        'myrepresentations',
        <GuestBlockedRoute>
            <userPagesModule.MyRepresentations />
        </GuestBlockedRoute>,
        'registrationRepresentation',
        'read'
    ),
    r('representationrequests', <Navigate to="/myrepresentations" replace />, 'registrationRepresentation', 'read'),
    r('representationapprovals', <representationModule.RepresentationApprovalsPage />, 'registrationRepresentation', 'read'),

    r('publictenders/search', <publicTenderModule.PublicTendersSearchList />, 'publicTenderList'),
    r('publictenders/view/:id', render(publicTenderModule.PublicTendersForm), 'publicTenderList'),
    r(
        'publictenders/:id/participate',
        <SupplierOnlyRoute>
            <publicTenderModule.PublicTendersSearchParticipate />
        </SupplierOnlyRoute>,
        'publicTenderList',
        'read'
    ),
    r(
        'publictenders/:id/proposal',
        <SupplierOnlyRoute>
            <publicTenderModule.PublicTendersViewProposal />
        </SupplierOnlyRoute>,
        'publicTenderList',
        'read'
    ),
    // Self-edit do próprio supplier. SupplierOnlyRoute garante que só supplier acessa (valida
    // entityType do card ativo); resource='userPages' evita guard de registrationCompany:write
    // que fornecedor não tem. Backend (rbac-policy PUT /suppliers/:id) continua sendo source
    // of truth para autorização — FE só roteia.
    r('suppliers/self/:id', <SupplierOnlyRoute>{render(companyModule.SuppliersForm)}</SupplierOnlyRoute>, 'userPages', 'read'),
    // Cadastro global de órgãos públicos: restrito ao administrador global (systemAdmin).
    // Removido de companyCrud para não herdar registrationCompany. Mantém-se resource
    // 'adminArea' por semântica; o gate efetivo é SystemAdminOnlyRoute.
    r('publicagencies', <SystemAdminOnlyRoute>{render(companyModule.PublicAgenciesList)}</SystemAdminOnlyRoute>, 'adminArea', 'read'),
    r('publicagencies/new', <SystemAdminOnlyRoute>{render(companyModule.PublicAgenciesForm)}</SystemAdminOnlyRoute>, 'adminArea', 'write'),
    r('publicagencies/:id', <SystemAdminOnlyRoute>{render(companyModule.PublicAgenciesForm)}</SystemAdminOnlyRoute>, 'adminArea', 'write'),
    // 6.12/precos: gerenciamento de taxa de participacao avulsa por orgao (global + override).
    // Acesso restrito ao admin (SystemAdminOnlyRoute) — o resource sozinho nao bloqueia acesso.
    r('precos-participacao', <SystemAdminOnlyRoute>{render(agencyPricingModule.AgencyPricing)}</SystemAdminOnlyRoute>, 'agencyPricing', 'read'),
    // 6.12 liberacao manual: pagamento feito fora da plataforma vira taxa da licitacao ou plano.
    // Admin global (SystemAdminOnlyRoute); o backend tambem exige systemAdmin.
    r('liberacao-manual', <SystemAdminOnlyRoute>{render(directa24PaymentModule.ManualReleasePage)}</SystemAdminOnlyRoute>, 'adminArea', 'read'),
    // F11 (PoC Chamamento 052/2026): gestao de pagamentos da cobranca por sucesso. Mesmo gate
    // do /precos-participacao (admin global); o backend exige financial-report:read.
    r('cobrancas-por-sucesso', <SystemAdminOnlyRoute>{render(successFeeChargesModule.SuccessFeeCharges)}</SystemAdminOnlyRoute>, 'financialReport', 'read'),
    // Relatorio financeiro por orgao (avulso). Restrito a admin.
    r('relatorios-financeiros', <SystemAdminOnlyRoute>{render(financialReportsModule.FinancialReports)}</SystemAdminOnlyRoute>, 'financialReport', 'read'),
    // Atas de Registro de Precos (SRP, PoC 60) — saldo/quantidades. Acesso por
    // permissao do resource (pregoeiro/agente/autoridade/admin), sem gate admin-only.
    r('atas-srp', render(priceRecordsModule.PriceRecords), 'priceRecords', 'read'),
    // Onboarding de orgao + equipe (S-T1) — wizard admin (endpoint e admin-only).
    r('onboarding-orgao', <SystemAdminOnlyRoute>{render(agencyOnboardingModule.AgencyOnboarding)}</SystemAdminOnlyRoute>, 'agencyOnboarding', 'read'),
    // Teste grátis de 7 dias (5.5): lista dos cadastros do site. Admin global; o backend também exige systemAdmin.
    r('testes-gratis', <SystemAdminOnlyRoute><TrialLeadsPage /></SystemAdminOnlyRoute>, 'adminArea', 'read'),
    // Pedidos de demonstração e de conversa com especialista feitos no site. Admin global; o backend também exige systemAdmin.
    r('pedidos-demonstracao', <SystemAdminOnlyRoute><DemoRequestsPage /></SystemAdminOnlyRoute>, 'adminArea', 'read'),
    // Self-edit do próprio órgão público. Análogo a /suppliers/self/:id — PublicAgencyOnlyRoute
    // valida entityType='public_agency' do card ativo. Backend default do módulo 3.2 aceita
    // representante_legal em PUT /publicagencies/:id (rbac-policy MODULE_POLICIES).
    r('publicagencies/self/:id', <PublicAgencyOnlyRoute>{render(companyModule.PublicAgenciesForm)}</PublicAgencyOnlyRoute>, 'userPages', 'read'),
    r(
        'publictenders/auction/view/:id',
        <GuestBlockedRoute>
            <biddingModule.AuctionSupplierDetailPage />
        </GuestBlockedRoute>,
        'publicTenderBidding',
        'read'
    ),
    r(
        'publictenders/auctioneer/view/:id',
        <AuctioneerOnlyRoute>
            <biddingModule.AuctioneerDetailPage />
        </AuctioneerOnlyRoute>,
        'publicTenderAuctioneer',
        'read'
    ),

    // Indicadores estrategicos do orgao (card 95). O backend restringe o relatorio por
    // pregoeiro a autoridade superior e ao admin; a tela mostra a mensagem do 403.
    r(
        'indicadores',
        <PublicAgencyOnlyRoute>{render(strategicIndicatorsModule.StrategicIndicators)}</PublicAgencyOnlyRoute>,
        'publicTenderAuctioneer',
        'read'
    ),
    // Drill-down dos indicadores (card 95): as licitacoes por tras de cada numero do painel.
    r(
        'indicadores/licitacoes',
        <PublicAgencyOnlyRoute>{render(strategicIndicatorsAgencyListModule.AgencyTenderList)}</PublicAgencyOnlyRoute>,
        'publicTenderAuctioneer',
        'read'
    ),
    r(
        'desempenho',
        <SupplierOnlyRoute>{render(strategicIndicatorsSupplierListModule.SupplierTenderList)}</SupplierOnlyRoute>,
        'publicTenderList',
        'read'
    ),
    // 4.0 Fase Interna — area do orgao publico (pregoeiro/equipe)
    r(
        'internal-phase',
        <PublicAgencyOnlyRoute>
            <InternalPhaseProjectsPage />
        </PublicAgencyOnlyRoute>,
        'publicTenderAuctioneer',
        'read'
    ),
    r(
        // param :projectId (nao :id) para nao ser reescrito em edit/:id / view/:id
        // por normalizeFormRoutes
        'internal-phase/:projectId',
        <PublicAgencyOnlyRoute>
            <InternalPhaseWorkspacePage />
        </PublicAgencyOnlyRoute>,
        'publicTenderAuctioneer',
        'read'
    ),
    r('publictenders/citizen/view/:id', <biddingModule.CitizenDetailPage />, 'publicTenderList', 'read'),

    r(
        'calendar',
        <GuestBlockedRoute>
            <calendarModule.CalendarView />
        </GuestBlockedRoute>,
        'publicTenderCalendar'
    ),

    r(
        'dialogcategories',
        <GuestBlockedRoute>
            <chatModule.DialogCategoriesList />
        </GuestBlockedRoute>,
        'publicTenderChat'
    ),
    r(
        'dialogcategories/new',
        <GuestBlockedRoute>
            <chatModule.DialogCategoriesForm />
        </GuestBlockedRoute>,
        'publicTenderChat',
        'write'
    ),
    r(
        'dialogcategories/:id',
        <GuestBlockedRoute>
            <chatModule.DialogCategoriesForm />
        </GuestBlockedRoute>,
        'publicTenderChat',
        'write'
    ),
    r(
        'dialogparticipants',
        <GuestBlockedRoute>
            <chatModule.DialogParticipantsList />
        </GuestBlockedRoute>,
        'publicTenderChat'
    ),
    r(
        'dialogparticipants/:id',
        <GuestBlockedRoute>
            <chatModule.DialogParticipantsForm />
        </GuestBlockedRoute>,
        'publicTenderForm',
        'write'
    ),
    r(
        'dialogparticipants/new',
        <GuestBlockedRoute>
            <chatModule.DialogParticipantsForm />
        </GuestBlockedRoute>,
        'publicTenderChat',
        'write'
    ),
    r(
        'dialogparticipants/:id',
        <GuestBlockedRoute>
            <chatModule.DialogParticipantsForm />
        </GuestBlockedRoute>,
        'publicTenderChat',
        'write'
    ),
    r(
        'dialogs',
        <GuestBlockedRoute>
            <chatModule.DialogsList />
        </GuestBlockedRoute>,
        'publicTenderChat'
    ),
    r(
        'dialogs/new',
        <GuestBlockedRoute>
            <chatModule.DialogsForm />
        </GuestBlockedRoute>,
        'publicTenderChat',
        'write'
    ),
    r(
        'dialogs/:id',
        <GuestBlockedRoute>
            <chatModule.DialogsForm />
        </GuestBlockedRoute>,
        'publicTenderChat',
        'write'
    ),
    r(
        'messages',
        <GuestBlockedRoute>
            <chatModule.MessagesList />
        </GuestBlockedRoute>,
        'publicTenderChat'
    ),
    r(
        'messages/new',
        <GuestBlockedRoute>
            <chatModule.MessagesForm />
        </GuestBlockedRoute>,
        'publicTenderChat',
        'write'
    ),
    r(
        'messages/:id',
        <GuestBlockedRoute>
            <chatModule.MessagesForm />
        </GuestBlockedRoute>,
        'publicTenderChat',
        'write'
    ),

    r(
        'ai-assistant-settings',
        <GuestBlockedRoute>
            <chatModule.AIAssistantSettingsForm />
        </GuestBlockedRoute>,
        'aiAssistantSettings',
        'write'
    ),

    r(
        'planschoice',
        <GuestBlockedRoute>
            <userPagesModule.SubscriptionPlansChoice />
        </GuestBlockedRoute>,
        'subscriptionOrder'
    ),
    r(
        'meu-plano',
        <GuestBlockedRoute>
            <MyPlanPage />
        </GuestBlockedRoute>,
        'subscriptionOrder'
    ),

    r('partners/:partnerId/credentials', <partnersModule.PartnerCredentialsList />, 'integrationPartners', 'read'),
    r('partners/:partnerId/credentials/new', <partnersModule.PartnerCredentialsForm />, 'integrationPartners', 'read'),
    r('partners/:partnerId/credentials/:id', <partnersModule.PartnerCredentialsForm />, 'integrationPartners', 'read'),
];

export const adminRouteConfigs: RouteConfig[] = [...commonRoutes, ...specialRoutes, ...crudGroups.flatMap(({ items, defaults }) => createCrudRoutes(items, defaults)), r('*', <PageNotFound />)];

const normalizeFormRoutes = (routes: RouteConfig[]): RouteConfig[] =>
    routes.flatMap((route) => {
        const path = route.path ?? '';

        // Keep routes that have an ID segment followed by extra path parts (e.g., /publictenders/:id/participate)
        if (/\/:[^/]+\//.test(path) && !/(^|\/)view\/:id(\/|$)/.test(path) && !/(^|\/)edit\/:id(\/|$)/.test(path)) {
            return [route];
        }

        if (/(^|\/)view\/:id(\/|$)/.test(path) || /(^|\/)edit\/:id(\/|$)/.test(path)) {
            return [route];
        }

        if (path.includes('/:id')) {
            return [
                { ...route, path: path.replace('/:id', '/edit/:id') },
                { ...route, path: path.replace('/:id', '/view/:id'), operation: undefined },
            ];
        }

        if (path.endsWith('/form')) {
            return [{ ...route, path: path.replace(/\/form$/, '/new') }];
        }

        if (/\/:[^/]+$/.test(path)) {
            return [route, { ...route, path: path.replace(/\/:[^/]+$/, '/edit/:id') }, { ...route, path: path.replace(/\/:[^/]+$/, '/view/:id'), operation: undefined }];
        }

        return [route];
    });

const applyResourceAccess = (routes: RouteConfig[]): RouteConfig[] =>
    routes.map((route) => {
        const element = route.resource && React.isValidElement(route.element) ? withResourceAccess(route.element, route.resource) : route.element;

        const children = route.children ? applyResourceAccess(route.children) : undefined;

        return {
            ...route,
            element,
            ...(children ? { children } : {}),
        };
    });

const adminRoutesWithVariants = normalizeFormRoutes(adminRouteConfigs);

function MpKeyDebugPage() {
    const key: string = import.meta.env.VITE_MERCADO_PAGO_PUBLIC_KEY ?? '';
    const masked = key.length > 14 ? `${key.slice(0, 8)}...${key.slice(-6)}` : key || '(não definido)';
    return (
        <div style={{ padding: '2rem', fontFamily: 'monospace' }}>
            <h2>Mercado Pago – chave pública</h2>
            <p>
                <strong>VITE_MERCADO_PAGO_PUBLIC_KEY:</strong> {masked}
            </p>
        </div>
    );
}

const rawAppRouteConfigs: RouteConfig[] = [
    { path: '/debug/mp-key', element: <MpKeyDebugPage /> },
    {
        path: '/login',
        element: <PublicAuthLayout />,
        children: [
            { index: true, element: <authUserModule.LoginForm /> },
            { path: 'register', element: <registerModule.RegisterForm /> },
            { path: 'registro', element: <registerModule.RegisterForm /> },
            { path: 'recuperar-senha', element: <forgotPasswordModule.ForgotPasswordForm /> },
        ],
    },
    { path: '/parceiros/integracao', element: <userPagesModule.PartnerIntegrationLanding /> },
    { path: '/verificar-relatorio', element: <VerifyReportPage /> },
    { path: '/verificar-ata', element: <VerifyAtaPage /> },
    // Manual do Fornecedor sem o layout do sistema: o Chrome sem tela imprime esta rota no PDF.
    { path: '/imprimir/manual-do-fornecedor', element: <manualFornecedorModule.ManualFornecedorImpressao /> },
    { path: '/pagamento/sucesso/:transactionId', element: <directa24PaymentModule.PaymentReturnPage kind="deposit" outcome="sucesso" /> },
    { path: '/pagamento/erro/:transactionId', element: <directa24PaymentModule.PaymentReturnPage kind="deposit" outcome="erro" /> },
    { path: '/pagamento/cancelado/:transactionId', element: <directa24PaymentModule.PaymentReturnPage kind="deposit" outcome="cancelado" /> },
    { path: '/assinatura/sucesso/:id', element: <directa24PaymentModule.PaymentReturnPage kind="subscription" outcome="sucesso" /> },
    { path: '/assinatura/erro/:id', element: <directa24PaymentModule.PaymentReturnPage kind="subscription" outcome="erro" /> },
    { path: '/assinatura/cancelado/:id', element: <directa24PaymentModule.PaymentReturnPage kind="subscription" outcome="cancelado" /> },
    { path: '/participar/:tenderId', element: <directa24PaymentModule.ParticipationPaymentPage /> },
    // F9/F10 (PoC Chamamento 052/2026): pagina apontada pelo link do e-mail de cobranca por
    // sucesso. Param nomeado :chargeId (e nao :id) para nao ser reescrito por normalizeFormRoutes.
    { path: '/cobranca-sucesso/:chargeId', element: <directa24PaymentModule.SuccessFeeChargePage /> },
    { path: '/auth/*', element: <Navigate to="/login" replace /> },
    { path: '/register', element: <registerModule.RegisterForm /> },
    { path: '/recuperar-senha', element: <forgotPasswordModule.ForgotPasswordForm /> },
    { path: '/logout', element: <LogoutPage /> },
    {
        path: '/',
        element: <Main />,
        children: [{ index: true, element: <GuestAwareHomeRedirect /> }, ...adminRoutesWithVariants],
    },
    { path: '/admin/*', element: <Navigate to="/" replace /> },
    { path: '/unauthorized', element: <Unauthorized /> },
    { path: 'recordnotfound', element: <RecordNotFound /> },
    { path: '*', element: <PageNotFound /> },
];

export const appRouteConfigs: RouteConfig[] = applyResourceAccess(rawAppRouteConfigs);
